import { useState } from 'react';
import { BrandMarkIcon } from '@/components/icons';
import ProgressRail from './ProgressRail';
import type { WorkflowProgressStep, WorkflowUiPhase } from './types';

const AI_PHASE_ICON_SIZE = 40;

const AI_PHASES: WorkflowUiPhase[] = ['thinking', 'working', 'almost-ready', 'saving'];

function ShieldIcon() {
  return (
    <svg className="flow-working-msg__shield" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M8 1.5L2.5 3.5v4.2c0 3.1 2.2 5.9 5.5 6.8 3.3-.9 5.5-3.7 5.5-6.8V3.5L8 1.5z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path
        d="M5.5 8.2l1.5 1.5 3.5-3.5"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Filled badge with shield + sparkles (Figma AI-Icon.svg). */
function ThinkingBadgeIcon() {
  return (
    <span className="flow-working-msg__badge flow-working-msg__badge--ai" aria-hidden>
      <BrandMarkIcon size={AI_PHASE_ICON_SIZE} />
    </span>
  );
}

function isAnimatedPhaseLabel(label: string) {
  const normalized = label.trim().toLowerCase();
  return (
    normalized.includes('thinking') ||
    normalized.includes('working') ||
    normalized.includes('almost') ||
    normalized.includes('ready') ||
    normalized.includes('saving') ||
    normalized.includes('adding') ||
    normalized.includes('issuing') ||
    normalized.includes('preparing')
  );
}

function shouldUseAiPhaseIcon(uiPhase: WorkflowUiPhase | undefined, phaseLabel: string) {
  if (uiPhase && AI_PHASES.includes(uiPhase)) return true;
  return isAnimatedPhaseLabel(phaseLabel);
}

export interface WorkingMessageProps {
  phaseLabel: string;
  uiPhase?: WorkflowUiPhase;
  cardTitle?: string;
  steps?: WorkflowProgressStep[];
  allComplete?: boolean;
  /** When false, only the phase label row is shown (e.g. Thinking). */
  showProgressCard?: boolean;
  /** Skeleton placeholder under the phase label (thinking only). */
  showSkeleton?: boolean;
  /** Compact skeleton while saving (form stays visible below). */
  showSavingSkeleton?: boolean;
}

export default function WorkingMessage({
  phaseLabel,
  uiPhase,
  cardTitle = "I'm working on your request",
  steps = [],
  allComplete = false,
  showProgressCard = true,
  showSkeleton = false,
  showSavingSkeleton = false,
}: WorkingMessageProps) {
  const [expanded, setExpanded] = useState(true);
  const showAiPhaseIcon = shouldUseAiPhaseIcon(uiPhase, phaseLabel);
  const animateLabel = showAiPhaseIcon || isAnimatedPhaseLabel(phaseLabel);

  return (
    <div className="flow-working-msg flow-thread__reveal" role="status" aria-live="polite">
      <div
        className={`flow-working-msg__phase${animateLabel ? ' flow-working-msg__phase--animated' : ''}${uiPhase === 'almost-ready' ? ' flow-working-msg__phase--almost-ready' : ''}`}
      >
        {showAiPhaseIcon ? (
          <ThinkingBadgeIcon />
        ) : (
          <ShieldIcon />
        )}
        <span
          className={`flow-working-msg__phase-label${animateLabel ? ' flow-working-msg__phase-label--shimmer' : ''}`}
        >
          {phaseLabel}
        </span>
      </div>

      {showSkeleton ? (
        <div className="flow-working-msg__skeleton">
          <div className="flow-working-msg__skeleton-line flow-working-msg__skeleton-line--title" />
          <div className="flow-working-msg__skeleton-lines">
            <div className="flow-working-msg__skeleton-line" />
            <div className="flow-working-msg__skeleton-line" />
            <div className="flow-working-msg__skeleton-line flow-working-msg__skeleton-line--short" />
          </div>
        </div>
      ) : showSavingSkeleton ? (
        <div className="flow-working-msg__skeleton flow-working-msg__skeleton--saving" aria-hidden>
          <div className="flow-working-msg__skeleton-line flow-working-msg__skeleton-line--title" />
          <div className="flow-working-msg__skeleton-lines">
            <div className="flow-working-msg__skeleton-line" />
            <div className="flow-working-msg__skeleton-line" />
          </div>
        </div>
      ) : showProgressCard && steps.length > 0 ? (
        <div
          className={`flow-working-msg__card${expanded ? ' flow-working-msg__card--expanded' : ' flow-working-msg__card--collapsed'}`}
        >
          <button
            type="button"
            className="flow-working-msg__card-head"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-controls="flow-working-progress"
            aria-label={expanded ? 'Hide progress steps' : 'Show progress steps'}
          >
            <span className="flow-working-msg__card-title">{cardTitle}</span>
            <svg
              className={`flow-working-msg__chevron${!expanded ? ' flow-working-msg__chevron--collapsed' : ''}`}
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden
            >
              <path
                d="M4 6l4 4 4-4"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {expanded ? (
            <div id="flow-working-progress" className="flow-working-msg__card-body">
              <ProgressRail
                steps={steps}
                allComplete={allComplete}
                ariaLabel="Workflow progress"
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

